import { useCallback, useEffect, useRef, useState } from "react";
import {
  addStroke,
  canvasToScreen,
  eraseStrokesNear,
  newId,
  rectsIntersect,
  screenToCanvas,
  strokePath,
  zoomAt,
  type Point,
  type Stroke,
} from "@worktools/shared";
import { useUi } from "../state/ui";
import { useBlockFrames, useLocalAwareness } from "../sync/hooks";
import { useBoard } from "../sync/BoardContext";
import { BlockShell } from "./BlockShell";
import { RemoteSelections } from "./RemoteSelections";
import { Cursors } from "./Cursors";
import { InkLayer } from "./InkLayer";
import { addBlockAt } from "../board/actions";
import { findScrollTarget } from "../ui/scroll";

type DragMode =
  | { kind: "none" }
  | { kind: "pan"; last: Point }
  | { kind: "marquee"; origin: Point; current: Point; additive: boolean }
  | { kind: "draw"; points: Point[] };

export function Canvas({ viewportRef }: { viewportRef: React.RefObject<HTMLDivElement | null> }) {
  const { doc, me } = useBoard();
  const frames = useBlockFrames();
  const camera = useUi((state) => state.camera);
  const setCamera = useUi((state) => state.setCamera);
  const tool = useUi((state) => state.tool);
  const selection = useUi((state) => state.selection);
  const setSelection = useUi((state) => state.setSelection);
  const penColor = useUi((state) => state.penColor);
  const penWidth = useUi((state) => state.penWidth);
  const openContextMenu = useUi((state) => state.openContextMenu);
  const closeContextMenu = useUi((state) => state.closeContextMenu);
  const setFollowing = useUi((state) => state.setFollowing);
  const setEditingBlockId = useUi((state) => state.setEditingBlockId);
  const publish = useLocalAwareness();

  const [drag, setDrag] = useState<DragMode>({ kind: "none" });
  const [spaceDown, setSpaceDown] = useState(false);
  const [liveStroke, setLiveStroke] = useState<Stroke | null>(null);
  const cursorFrame = useRef<number | null>(null);

  const toCanvas = useCallback(
    (event: { clientX: number; clientY: number }): Point => {
      const rect = viewportRef.current?.getBoundingClientRect();
      return screenToCanvas(
        { x: event.clientX - (rect?.left ?? 0), y: event.clientY - (rect?.top ?? 0) },
        camera,
      );
    },
    [camera, viewportRef],
  );

  useEffect(() => {
    const down = (event: KeyboardEvent) => {
      if (event.code === "Space" && !isTypingTarget(event.target)) setSpaceDown(true);
    };
    const up = (event: KeyboardEvent) => {
      if (event.code === "Space") setSpaceDown(false);
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button === 2) return;
    closeContextMenu();
    setEditingBlockId(null);
    const isBackground = event.target === event.currentTarget || (event.target as HTMLElement).dataset.canvasBackground === "true";
    if (!isBackground) return;

    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    setFollowing(null);

    if (event.button === 1 || spaceDown || tool === "pan") {
      setDrag({ kind: "pan", last: { x: event.clientX, y: event.clientY } });
      return;
    }

    const point = toCanvas(event);

    if (tool === "pen" || tool === "marker") {
      setDrag({ kind: "draw", points: [point] });
      setLiveStroke({
        id: newId("st"),
        color: penColor,
        width: tool === "marker" ? penWidth * 3 : penWidth,
        tool,
        authorId: me.id,
        points: [point],
      });
      return;
    }

    if (tool === "eraser") {
      eraseStrokesNear(doc, point, 8 / camera.zoom);
      setDrag({ kind: "draw", points: [point] });
      return;
    }

    if (tool === "grant") return;

    if (!event.shiftKey) setSelection([]);
    setDrag({ kind: "marquee", origin: point, current: point, additive: event.shiftKey });
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const point = toCanvas(event);

    if (cursorFrame.current === null) {
      cursorFrame.current = requestAnimationFrame(() => {
        cursorFrame.current = null;
        publish({ cursor: point });
      });
    }

    if (drag.kind === "pan") {
      const delta = {
        x: (event.clientX - drag.last.x) / camera.zoom,
        y: (event.clientY - drag.last.y) / camera.zoom,
      };
      setCamera((current) => ({ ...current, x: current.x + delta.x, y: current.y + delta.y }));
      setDrag({ kind: "pan", last: { x: event.clientX, y: event.clientY } });
      return;
    }

    if (drag.kind === "marquee") {
      const next = { ...drag, current: point };
      setDrag(next);
      const box = marqueeRect(next.origin, next.current);
      const hits = frames
        .filter((frame) => rectsIntersect(box, frame))
        .map((frame) => frame.id);
      setSelection(next.additive ? [...new Set([...selection, ...hits])] : hits);
      return;
    }

    if (drag.kind === "draw") {
      if (tool === "eraser") {
        eraseStrokesNear(doc, point, 8 / camera.zoom);
        return;
      }
      setLiveStroke((current) => (current ? { ...current, points: [...current.points, point] } : current));
    }
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (liveStroke && liveStroke.points.length > 0) addStroke(doc, liveStroke);
    setLiveStroke(null);
    setDrag({ kind: "none" });
  };

  const onWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    // A block that can still scroll keeps the wheel; the canvas only pans otherwise.
    if (!event.ctrlKey && !event.metaKey && findScrollTarget(event.target, viewportRef.current, event.deltaY)) {
      return;
    }
    const rect = viewportRef.current?.getBoundingClientRect();
    const local = { x: event.clientX - (rect?.left ?? 0), y: event.clientY - (rect?.top ?? 0) };
    if (event.ctrlKey || event.metaKey) {
      setCamera((current) => zoomAt(current, local, current.zoom * Math.exp(-event.deltaY / 260)));
      return;
    }
    setCamera((current) => ({
      ...current,
      x: current.x - event.deltaX / current.zoom,
      y: current.y - event.deltaY / current.zoom,
    }));
  };

  const onContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    const isBackground = event.target === event.currentTarget || (event.target as HTMLElement).dataset.canvasBackground === "true";
    if (!isBackground) return;
    event.preventDefault();
    openContextMenu({
      screen: { x: event.clientX, y: event.clientY },
      target: { kind: "canvas", canvasPoint: toCanvas(event) },
    });
  };

  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    const type = event.dataTransfer.getData("application/x-worktools-block");
    if (!type) return;
    event.preventDefault();
    const id = addBlockAt(doc, type as any, toCanvas(event), me.id);
    setSelection([id]);
  };

  const gridSize = 24 * camera.zoom;
  const cursorClass =
    drag.kind === "pan"
      ? "viewport--panning"
      : tool === "pan" || spaceDown
        ? "viewport--pan"
        : tool === "select"
          ? ""
          : tool === "grant"
            ? "viewport--grant"
            : "viewport--draw";

  return (
    <div
      ref={viewportRef}
      className={`viewport ${cursorClass}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onWheel={onWheel}
      onContextMenu={onContextMenu}
      onDragOver={(event) => event.preventDefault()}
      onDrop={onDrop}
      onPointerLeave={() => publish({ cursor: null })}
      style={{
        backgroundImage: "radial-gradient(var(--line-strong) 1px, transparent 1px)",
        backgroundSize: `${gridSize}px ${gridSize}px`,
        backgroundPosition: `${camera.x * camera.zoom}px ${camera.y * camera.zoom}px`,
      }}
      data-canvas-background="true"
    >
      <div
        className="world"
        data-canvas-background="true"
        style={{ transform: `translate(${camera.x * camera.zoom}px, ${camera.y * camera.zoom}px) scale(${camera.zoom})` }}
      >
        <InkLayer live={liveStroke} />
        {frames.map((frame) => (
          <BlockShell key={frame.id} frame={frame} toCanvas={toCanvas} />
        ))}
        <RemoteSelections frames={frames} />
      </div>

      {drag.kind === "marquee" && <Marquee origin={drag.origin} current={drag.current} camera={camera} />}
      {liveStroke && null}
      <Cursors />
    </div>
  );
}

function Marquee({ origin, current, camera }: { origin: Point; current: Point; camera: { x: number; y: number; zoom: number } }) {
  const rect = marqueeRect(origin, current);
  const topLeft = canvasToScreen({ x: rect.x, y: rect.y }, camera);
  return (
    <div
      className="marquee"
      style={{
        left: topLeft.x,
        top: topLeft.y,
        width: rect.w * camera.zoom,
        height: rect.h * camera.zoom,
      }}
    />
  );
}

function marqueeRect(a: Point, b: Point) {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(a.x - b.x),
    h: Math.abs(a.y - b.y),
  };
}

export function isTypingTarget(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (!element) return false;
  return (
    element.tagName === "INPUT" ||
    element.tagName === "TEXTAREA" ||
    element.isContentEditable === true
  );
}

export { strokePath };
